import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";

/**
 * Configuración del módulo de recordatorios de cuotas (por empresa).
 *
 * - Activación / desactivación.
 * - Días de aviso previo (cuotas a vencer) y días de aviso vencido.
 * - SMTP por empresa.
 * - Plantillas dinámicas para asunto y cuerpo (placeholders {variable}).
 * - Botón para enviar correo de prueba.
 *
 * Persistencia: backend en JSON por CompanyDB (ver backend/data/recordatorios).
 */
const RecordatoriosConfiguracion = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [config, setConfig] = useState(null);
  const [variables, setVariables] = useState([]);

  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const userCompanyDb = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
    return user?.CompanyDB;
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const [cfgRes, varsRes] = await Promise.all([
        apiClient.get("/recordatorios/configuracion", {
          params: { CompanyDB: userCompanyDb },
        }),
        apiClient.get("/recordatorios/variables"),
      ]);
      setConfig(cfgRes?.data || null);
      setVariables(Array.isArray(varsRes?.data) ? varsRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (field, value) => {
    setConfig((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const setSmtp = (field, value) => {
    setConfig((prev) => ({
      ...(prev || {}),
      smtp: { ...((prev || {}).smtp || {}), [field]: value },
    }));
  };

  const parseDiasList = (str) =>
    String(str || "")
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isFinite(n) && n >= 0);

  const onSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        ...config,
        dias_aviso_previo: Array.isArray(config.dias_aviso_previo)
          ? config.dias_aviso_previo
          : parseDiasList(config.dias_aviso_previo),
        dias_aviso_vencido: Array.isArray(config.dias_aviso_vencido)
          ? config.dias_aviso_vencido
          : parseDiasList(config.dias_aviso_vencido),
      };
      // No mandar password vacío al backend lo machaque (backend lo conserva si viene "")
      const res = await apiClient.put("/recordatorios/configuracion", payload, {
        params: { CompanyDB: userCompanyDb },
      });
      setConfig(res?.data || payload);
      setMessage("Configuración guardada correctamente.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error guardando configuración");
    } finally {
      setSaving(false);
    }
  };

  const onSendTest = async () => {
    setError("");
    setMessage("");
    if (!testEmail) {
      setError("Ingrese un correo destino para la prueba.");
      return;
    }
    setSendingTest(true);
    try {
      await apiClient.post(
        "/recordatorios/test-email",
        {
          to: testEmail,
          smtp_override: config?.smtp || {},
        },
        { params: { CompanyDB: userCompanyDb } }
      );
      setMessage(`Correo de prueba enviado a ${testEmail}.`);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error enviando prueba");
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) return <div>Cargando configuración...</div>;
  if (!config) return <div className="alert alert-warning">Sin configuración disponible.</div>;

  const diasPrevText = Array.isArray(config.dias_aviso_previo)
    ? config.dias_aviso_previo.join(", ")
    : config.dias_aviso_previo || "";
  const diasVencText = Array.isArray(config.dias_aviso_vencido)
    ? config.dias_aviso_vencido.join(", ")
    : config.dias_aviso_vencido || "";

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h3 className="card-title mb-2 text-center">Recordatorios de cuotas - Configuración</h3>
        <hr className="mt-0 mb-4" />

        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <form onSubmit={onSave}>
          {/* Activación */}
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Estado</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="switch-activo"
                  checked={!!config.activo}
                  onChange={(e) => setField("activo", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="switch-activo">
                  {config.activo ? "Activo" : "Inactivo"}
                </label>
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Horario de envío diario</label>
              <input
                type="time"
                className="form-control"
                value={config.horario_envio || "08:00"}
                onChange={(e) => setField("horario_envio", e.target.value)}
              />
              <div className="form-text">Hora a la que el scheduler dispara los envíos (servidor).</div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Incluir cuotas ya pagadas</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="switch-pagadas"
                  checked={!!config.incluir_cuotas_pagadas}
                  onChange={(e) => setField("incluir_cuotas_pagadas", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="switch-pagadas">
                  {config.incluir_cuotas_pagadas ? "Sí" : "No"}
                </label>
              </div>
              <div className="form-text">Por defecto se omiten cuotas con factura asociada.</div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Días de aviso previo (a vencer)</label>
              <input
                className="form-control"
                value={diasPrevText}
                onChange={(e) => setField("dias_aviso_previo", e.target.value)}
                placeholder="Ej: 7, 3, 1"
              />
              <div className="form-text">Días faltantes en los que se notifica antes del vencimiento.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label">Días de aviso vencido</label>
              <input
                className="form-control"
                value={diasVencText}
                onChange={(e) => setField("dias_aviso_vencido", e.target.value)}
                placeholder="Ej: 1, 7, 15, 30"
              />
              <div className="form-text">Días de atraso en los que se notifica luego del vencimiento.</div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Nombre del remitente</label>
              <input
                className="form-control"
                value={config.remitente_nombre || ""}
                onChange={(e) => setField("remitente_nombre", e.target.value)}
                placeholder="Ej: Cobranzas Altius Group"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Copia oculta (CCO)</label>
              <input
                className="form-control"
                value={config.copia_a || ""}
                onChange={(e) => setField("copia_a", e.target.value)}
                placeholder="cobranzas@empresa.com, control@empresa.com"
              />
              <div className="form-text">Separar por coma o punto y coma. Opcional.</div>
            </div>
          </div>

          <h5 className="mt-4 mb-2">Servidor SMTP</h5>
          <hr className="mt-0 mb-3" />
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Host</label>
              <input
                className="form-control"
                value={config.smtp?.host || ""}
                onChange={(e) => setSmtp("host", e.target.value)}
                placeholder="smtp.office365.com"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Puerto</label>
              <input
                type="number"
                className="form-control"
                value={config.smtp?.port ?? 587}
                onChange={(e) => setSmtp("port", parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">STARTTLS</label>
              <div className="form-check form-switch mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={!!config.smtp?.use_tls}
                  onChange={(e) => setSmtp("use_tls", e.target.checked)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">SSL directo</label>
              <div className="form-check form-switch mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={!!config.smtp?.use_ssl}
                  onChange={(e) => setSmtp("use_ssl", e.target.checked)}
                />
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Usuario</label>
              <input
                className="form-control"
                value={config.smtp?.username || ""}
                onChange={(e) => setSmtp("username", e.target.value)}
                placeholder="cobranzas@empresa.com"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">
                Contraseña{" "}
                {config.smtp?.password_set && !config.smtp?.password && (
                  <small className="text-success">(guardada — dejar vacío para no cambiar)</small>
                )}
              </label>
              <input
                type="password"
                className="form-control"
                value={config.smtp?.password || ""}
                onChange={(e) => setSmtp("password", e.target.value)}
                placeholder={config.smtp?.password_set ? "••••••••" : ""}
                autoComplete="new-password"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">From (correo remitente)</label>
              <input
                className="form-control"
                value={config.smtp?.from_email || ""}
                onChange={(e) => setSmtp("from_email", e.target.value)}
                placeholder="cobranzas@empresa.com"
              />
            </div>

            <div className="col-12">
              <div className="d-flex flex-wrap gap-2 align-items-end">
                <div className="flex-grow-1" style={{ minWidth: 240 }}>
                  <label className="form-label">Probar SMTP - enviar a</label>
                  <input
                    type="email"
                    className="form-control"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="prueba@dominio.com"
                  />
                </div>
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  onClick={onSendTest}
                  disabled={sendingTest}
                >
                  {sendingTest ? "Enviando..." : "Enviar prueba"}
                </button>
              </div>
            </div>
          </div>

          <h5 className="mt-4 mb-2">Plantillas - cuotas a vencer</h5>
          <hr className="mt-0 mb-3" />
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Asunto</label>
              <input
                className="form-control"
                value={config.asunto_proximas || ""}
                onChange={(e) => setField("asunto_proximas", e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Cuerpo</label>
              <textarea
                className="form-control"
                rows={6}
                value={config.cuerpo_proximas || ""}
                onChange={(e) => setField("cuerpo_proximas", e.target.value)}
              />
            </div>
          </div>

          <h5 className="mt-4 mb-2">Plantillas - cuotas vencidas</h5>
          <hr className="mt-0 mb-3" />
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Asunto</label>
              <input
                className="form-control"
                value={config.asunto_vencidas || ""}
                onChange={(e) => setField("asunto_vencidas", e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Cuerpo</label>
              <textarea
                className="form-control"
                rows={6}
                value={config.cuerpo_vencidas || ""}
                onChange={(e) => setField("cuerpo_vencidas", e.target.value)}
              />
            </div>
          </div>

          <h5 className="mt-4 mb-2">Plantillas - varias cuotas vencidas (consolidado)</h5>
          <hr className="mt-0 mb-2" />
          <p className="text-muted small">
            Se usa cuando un contrato tiene más de una cuota vencida: se envía un único
            correo que lista todas las cuotas y advierte que se siguen generando
            intereses hasta el pago. Variables propias: {"{cantidad_cuotas}"},{" "}
            {"{detalle_cuotas}"}, {"{monto_total}"}.
          </p>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Asunto</label>
              <input
                className="form-control"
                value={config.asunto_vencidas_multiple || ""}
                onChange={(e) => setField("asunto_vencidas_multiple", e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Cuerpo</label>
              <textarea
                className="form-control"
                rows={10}
                value={config.cuerpo_vencidas_multiple || ""}
                onChange={(e) => setField("cuerpo_vencidas_multiple", e.target.value)}
              />
            </div>
          </div>

          {variables.length > 0 && (
            <div className="alert alert-info mt-3">
              <strong>Variables disponibles:</strong>{" "}
              <span className="small">
                Use estas claves entre llaves dentro del asunto y cuerpo.
              </span>
              <div className="mt-2 d-flex flex-wrap gap-2">
                {variables.map((v) => (
                  <span
                    key={v.clave}
                    className="badge bg-secondary"
                    title={v.descripcion}
                    style={{ cursor: "help" }}
                  >
                    {v.clave} <span className="text-light-emphasis">— {v.descripcion}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex gap-2 mt-4">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? "Guardando..." : "Guardar configuración"}
            </button>
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={loadAll}
              disabled={saving}
            >
              Refrescar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordatoriosConfiguracion;
